import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import { LinearProgress, Grid, TextField, Button, Stack, Typography } from '@mui/material'
import { RegisteredCheckBox } from '../../../../components/fields/CheckBox'
import { InvestorsFieldsSanityCheckProps } from './AdminInvestorFieldsSanityCheck'
import { InvestorsTable } from './AdminInvestorsTable'
import { ShouldShow } from '../../../../components/entities/IssueType'
import { FormIssueEntity } from '../../types/FormIssueEntity'

export type validateFn = (value: string, formValues: InvestorForm) => boolean

export type InvestorForm = {
    name: string
    amountInPercentage: string | null
    amountInCash: string | null
    feeLevel?: string | null
    leo?: boolean | null
    isSeller: boolean | null
    /** Företrädesemission / IPO: teckningsåtagande utan teckningsrätter (endast nivå 0) */
    withoutRights?: boolean | null
    putOption?: boolean | null
    /** Övergarant, ömsesidigt exklusivt mot undergarant (putOption) */
    primaryGuarantor?: boolean
}

export type InvestorFormWithId = InvestorForm & { tempId?: string; id?: string }

/**
 * Cleans the value string. Removes leading and trailing whitespace. If the value is numerical, it also
 * replaces commas with dots and removes any non-numeric characters (except dots).
 *
 * @param {string} value the value to clean
 * @param {boolean} isNumerical flag to indicate if the value is numerical
 */
const cleanValue = (value: string, isNumerical?: boolean) => {
    if (!value) return value
    value = value.trim()
    // don't allow more than one space character in a row
    value = value.replace(/\s{2,}/g, ' ')
    if (isNumerical) {
        value = value.replace(',', '.').replace(/[^0-9.]/g, '')
    }
    return value
}

function parseBooleanCell(value: string | undefined): boolean {
    if (value == null || value === '') return false
    return value === '1' || value === 'true' || (value !== '0' && !!value)
}

function buildMassInputColumnKeys({
    entityType,
    isPercentageEnabled,
    isFeeLevelEnabled,
    isLeoEnabled,
    isSellerEnabled,
    isPutOptionEnabled,
    isWithoutRightsEnabled,
}: {
    entityType: 'issue' | 'warrant' | 'convertible'
    isPercentageEnabled: boolean
    isFeeLevelEnabled: boolean
    isLeoEnabled: boolean
    isSellerEnabled: boolean
    isPutOptionEnabled: boolean
    isWithoutRightsEnabled: boolean
}): string[] {
    if (entityType === 'convertible') {
        return ['id', 'name', 'amount_in_percentage', 'amount_in_cash']
    }
    if (entityType === 'warrant') {
        return [
            'id',
            'name',
            'amount_in_cash',
            ...(isFeeLevelEnabled ? ['fee_level'] : []),
            ...(isPutOptionEnabled ? ['put_option', 'primary_guarantor'] : []),
        ]
    }
    const cols = ['id', 'name']
    if (isPercentageEnabled) cols.push('amount_in_percentage')
    cols.push('amount_in_cash')
    if (isFeeLevelEnabled) cols.push('fee_level')
    if (isLeoEnabled) cols.push('leo')
    if (isSellerEnabled) cols.push('is_seller')
    if (isPutOptionEnabled) {
        cols.push('put_option', 'primary_guarantor')
    }
    if (isWithoutRightsEnabled) cols.push('without_rights')
    return cols
}

function isCommaString(textStr: string) {
    const rows = textStr.trim().split('\n')

    if (rows.length > 1) return false

    return rows[0]?.includes(',')
}

function parseCommaString(commaString: string) {
    const investorNames = commaString.trim().split(', ')

    const lastInvestorName = investorNames.pop() as string
    const correctionChecks = [' och ', ' samt ', ' and ']

    let matchExists = null

    for (const correctionCheck of correctionChecks) {
        if (lastInvestorName?.includes(correctionCheck)) {
            matchExists = correctionCheck
            break
        }
    }

    if (matchExists) {
        const correctedNames = [
            lastInvestorName.slice(0, lastInvestorName.lastIndexOf(matchExists)),
            lastInvestorName.slice(lastInvestorName.lastIndexOf(matchExists) + matchExists.length),
        ]

        investorNames.push(...correctedNames)
    } else {
        investorNames.push(lastInvestorName)
    }

    return investorNames
}

/**
 * Creates a list of objects from a tab separated string, each line
 * representing an object, and the first line has the keys.
 *
 * @param {string} tabString the tab separated string
 */
function parseTabString(tabString: string) {
    if (!tabString) return []
    const numericalFields = ['amount_in_percentage', 'amount_in_cash', 'fee_level']
    const lines = tabString.split('\n')
    const keys = lines[0].split('\t')
    const objects = lines.slice(1).map((line) => {
        const values = line.split('\t')
        const obj: Record<string, unknown> = {}
        keys.forEach((key, i) => {
            const value = cleanValue(values[i], numericalFields.includes(key))
            obj[key] = value
        })
        return obj
    })
    // remove all empty rows
    return objects.filter((obj) => Object.values(obj).some((v) => v)) as Record<string, string>[]
}

function tabRowToInvestor(
    row: Record<string, string>,
    {
        isPercentageEnabled,
        isFeeLevelEnabled,
        isLeoEnabled,
        isSellerEnabled,
        isPutOptionEnabled,
        isWithoutRightsEnabled,
    }: {
        isPercentageEnabled: boolean
        isFeeLevelEnabled: boolean
        isLeoEnabled: boolean
        isSellerEnabled: boolean
        isPutOptionEnabled: boolean
        isWithoutRightsEnabled: boolean
    },
): InvestorForm {
    const isSeller = isSellerEnabled && parseBooleanCell(row.is_seller)
    const feeLevelRaw = row.fee_level
    const feeLevel =
        isFeeLevelEnabled && feeLevelRaw !== undefined && feeLevelRaw !== ''
            ? parseInt(feeLevelRaw, 10)
            : isFeeLevelEnabled
              ? null
              : undefined

    const investor: InvestorForm = {
        name: row.name ?? '',
        amountInPercentage: null,
        amountInCash: null,
        isSeller: isSellerEnabled ? isSeller : null,
    }

    if (!isSeller) {
        if (isPercentageEnabled && row.amount_in_percentage) {
            investor.amountInPercentage = parseFloat(row.amount_in_percentage)
        }
        if (row.amount_in_cash) {
            investor.amountInCash = parseInt(row.amount_in_cash, 10)
        }
        if (isFeeLevelEnabled) {
            investor.feeLevel = feeLevel ?? null
        }
        if (isLeoEnabled) {
            investor.leo = parseBooleanCell(row.leo)
        }
        if (isPutOptionEnabled) {
            investor.putOption = parseBooleanCell(row.put_option)
            investor.primaryGuarantor = parseBooleanCell(row.primary_guarantor)
        }
        if (isWithoutRightsEnabled && feeLevel === 0) {
            investor.withoutRights = parseBooleanCell(row.without_rights)
        } else if (isWithoutRightsEnabled) {
            investor.withoutRights = false
        }
    } else {
        investor.feeLevel = isFeeLevelEnabled ? null : undefined
        investor.leo = isLeoEnabled ? null : undefined
        investor.withoutRights = isWithoutRightsEnabled ? false : undefined
        investor.putOption = isPutOptionEnabled ? false : undefined
        investor.primaryGuarantor = isPutOptionEnabled ? false : undefined
    }

    if (row.id) {
        investor.id = row.id
    }

    return investor
}

function validateImportedInvestorAmounts(validateAmount: validateFn, investor: InvestorForm): boolean {
    if (investor.isSeller) return true
    const cash = investor.amountInCash ?? ''
    const pct = investor.amountInPercentage ?? ''
    if (cash !== '' && cash !== null && !validateAmount(cash, investor)) return false
    if (pct !== '' && pct !== null && !validateAmount(pct, investor)) return false
    if (cash && pct) return false
    return true
}

function validateImportedGuarantorFields(investor: InvestorForm, isPutOptionEnabled: boolean): boolean {
    if (!isPutOptionEnabled) return true
    if (investor.putOption && investor.primaryGuarantor) return false
    if (investor.isSeller) {
        return !investor.putOption && !investor.primaryGuarantor
    }
    const fee = investor.feeLevel
    if (investor.putOption || investor.primaryGuarantor) {
        if (fee === null || fee === undefined || Number(fee) === 0) return false
    }
    return true
}

function validateImportedWithoutRights(investor: InvestorForm, isWithoutRightsEnabled: boolean): boolean {
    if (!isWithoutRightsEnabled || !investor.withoutRights) return true
    if (investor.isSeller) return false
    return investor.feeLevel !== null && investor.feeLevel !== undefined && Number(investor.feeLevel) === 0
}

function getImportedInvestorValidationError(
    investor: InvestorForm,
    {
        validateName,
        validateAmount,
        validateFeeLevel,
        validateLeo,
        isPutOptionEnabled,
        isWithoutRightsEnabled,
    }: {
        validateName: (value: string) => boolean
        validateAmount: validateFn
        validateFeeLevel: (value: string | null | undefined, values: InvestorForm) => boolean
        validateLeo: validateFn
        isPutOptionEnabled: boolean
        isWithoutRightsEnabled: boolean
    },
): string | null {
    if (!validateName(investor.name)) {
        return 'Namn saknas eller är ogiltigt.'
    }
    if (isPutOptionEnabled && investor.putOption && investor.primaryGuarantor) {
        return 'Undergarant (put_option) och övergarant (primary_guarantor) får inte båda vara 1 på samma rad.'
    }
    if (!validateImportedInvestorAmounts(validateAmount, investor)) {
        return 'Ogiltigt belopp (t.ex. både procent och kronor, eller belopp 0).'
    }
    if (!validateFeeLevel(investor.feeLevel, investor)) {
        return 'Ogiltig nivå (fee_level) — måste finnas bland arvoden på formuläret.'
    }
    if (!validateLeo('', investor)) {
        return 'Leo får inte vara satt för den här emissionstypen.'
    }
    if (!validateImportedGuarantorFields(investor, isPutOptionEnabled)) {
        return 'Ogiltig garant (undergarant/övergarant kräver nivå ≠ 0 och gäller inte säljare).'
    }
    if (!validateImportedWithoutRights(investor, isWithoutRightsEnabled)) {
        return 'TÅ utan rätter (without_rights) gäller bara vid nivå 0.'
    }
    return null
}

interface InvestorsFieldsProps {
    issue?: FormIssueEntity
    entityType: 'issue' | 'warrant' | 'convertible'
    acceptedFeeLevels: number[]
    currency: string | null
    isFeeLevelEnabled: boolean
    isLeoEnabled: boolean
    /** Företrädesemission / IPO: redigera TÅ utan rätter */
    isWithoutRightsEnabled?: boolean
    isPercentageEnabled?: boolean
    isSellerEnabled?: boolean
    isPutOptionEnabled?: boolean
    noInvestorsNamedChecked: boolean
    sanityChecker: (investors: InvestorFormWithId[]) => InvestorsFieldsSanityCheckProps
    setNoInvestorsNamedChecked?: (val: boolean) => void
    validateAmount: validateFn
    validateLeo: validateFn
}

const setTempId = (investor: InvestorFormWithId) => {
    return {
        ...investor,
        tempId: investor.tempId || investor.id || uuidv4(),
    }
}

function InvestorsFields({
    issue,
    entityType,
    acceptedFeeLevels,
    currency,
    isFeeLevelEnabled,
    isLeoEnabled,
    isWithoutRightsEnabled = false,
    isPercentageEnabled = true,
    isSellerEnabled = true,
    isPutOptionEnabled = false,
    noInvestorsNamedChecked,
    sanityChecker,
    setNoInvestorsNamedChecked,
    validateAmount,
    validateLeo,
}: InvestorsFieldsProps) {
    const formMethods = useFormContext()
    const formValues = formMethods.getValues()
    const [investors, setInvestors] = useState<InvestorFormWithId[]>(
        ((formValues.investors as InvestorFormWithId[]) || []).map(setTempId),
    )
    const massInputColumnKeys = useMemo(
        () =>
            buildMassInputColumnKeys({
                entityType,
                isPercentageEnabled,
                isFeeLevelEnabled,
                isLeoEnabled,
                isSellerEnabled,
                isPutOptionEnabled,
                isWithoutRightsEnabled,
            }),
        [
            entityType,
            isPercentageEnabled,
            isFeeLevelEnabled,
            isLeoEnabled,
            isSellerEnabled,
            isPutOptionEnabled,
            isWithoutRightsEnabled,
        ],
    )
    const massInputHeaderLine = massInputColumnKeys.join('\t')
    const [inputInvestors, setInputInvestors] = useState(massInputHeaderLine)
    const [showInputArea, setShowInputArea] = useState(false)
    const [massInputError, setMassInputError] = useState<string | null>(null)

    const allowCommaSeparatedNames =
        issue && ['Directed issue', 'ABB Directed issue', 'Non-cash issue'].includes(issue.type)

    const sortInvestors = (investorList: InvestorForm[]) => {
        // sort by feeLevel ASC, amountInCash DESC, amountInPercentage DESC
        investorList.sort((a, b) => {
            if (a.feeLevel !== b.feeLevel) return a.feeLevel < b.feeLevel ? -1 : 1
            if (a.amountInCash !== b.amountInCash) return b.amountInCash - a.amountInCash
            return b.amountInPercentage - a.amountInPercentage
        })
    }
    const setFormInvestors = useCallback(
        (i: InvestorFormWithId[]) => {
            if (JSON.stringify(i) === JSON.stringify(investors)) return

            const newInvestors = i.map(setTempId) // make sure all investors have a tempId
            sortInvestors(newInvestors)
            setInvestors(newInvestors)
            // issue.investors = investors
            const formInvestors = newInvestors.map((p) => {
                const { tempId, ...rest } = p
                return rest
            })
            formMethods.setValue('investors', formInvestors)
        },
        [formMethods, investors],
    )
    useEffect(() => {
        setFormInvestors(investors)
    }, [investors, setFormInvestors])

    useEffect(() => {
        setInputInvestors((prev) => {
            const lines = prev.split('\n')
            if (lines.length <= 1) {
                return massInputHeaderLine
            }
            return [massInputHeaderLine, ...lines.slice(1)].join('\n')
        })
    }, [massInputHeaderLine])

    if (!investors) return <LinearProgress />

    const validateFeeLevel = (value: string | null | undefined, values: InvestorForm) => {
        if (values.isSeller) return true
        if (!isFeeLevelEnabled) return true

        if (value === null) return false
        if (value === undefined) return false
        if (+value === 0) return true

        return acceptedFeeLevels.includes(+value)
    }
    const validateName = (value: string) => {
        if (
            entityType === 'warrant' ||
            (issue && !['ABB Directed issue', 'Directed issue', 'Non-cash issue'].includes(issue.type))
        )
            return !!value

        if (value?.match(/^\x3F$/)) return false

        return !!value
    }

    const mapImportFlags = {
        isPercentageEnabled,
        isFeeLevelEnabled,
        isLeoEnabled,
        isSellerEnabled,
        isPutOptionEnabled,
        isWithoutRightsEnabled,
    }

    const importValidationContext = {
        validateName,
        validateAmount,
        validateFeeLevel,
        validateLeo,
        isPutOptionEnabled,
        isWithoutRightsEnabled,
    }

    const handleTransfer = () => {
        setMassInputError(null)

        if (isCommaString(inputInvestors)) {
            if (!allowCommaSeparatedNames) {
                setMassInputError('Kommaseparerade namn gäller bara riktad emission, ABB riktad och apportemission.')
                return
            }
            const names = parseCommaString(inputInvestors)
            if (names.some((name) => !validateName(name))) {
                setMassInputError('Alla namn måste vara ifyllda (och får inte vara enbart "?").')
                return
            }
            const inv = [
                ...investors,
                ...names.map((name) => ({
                    name,
                    isSeller: false,
                    amountInCash: null,
                    amountInPercentage: null,
                })),
            ]
            setFormInvestors(inv)
            setInputInvestors(massInputHeaderLine)
            return
        }

        const rows = parseTabString(inputInvestors)
        if (rows.length === 0) {
            setMassInputError('Lägg till minst en datarad under rubrikraden (tab-separerad).')
            return
        }

        const expectedKeys = massInputColumnKeys.join('\t')
        const actualKeys = inputInvestors.split('\n')[0]?.trim()
        if (actualKeys !== expectedKeys) {
            setMassInputError(`Rubrikraden ska matcha mallen. Förväntat: ${massInputColumnKeys.join(', ')}.`)
            return
        }

        const newInvestors: InvestorForm[] = rows.map((row) => tabRowToInvestor(row, mapImportFlags))

        for (let i = 0; i < newInvestors.length; i++) {
            const validationError = getImportedInvestorValidationError(newInvestors[i], importValidationContext)
            if (validationError) {
                setMassInputError(`Rad ${i + 2}: ${validationError}`)
                return
            }
        }

        const aggregatedInvestors = investors.concat(newInvestors)
        setFormInvestors(aggregatedInvestors)
        setInputInvestors(massInputHeaderLine)
    }
    const nullEmptyStrings = (obj: Record<string, unknown>) => {
        Object.keys(obj).forEach((key) => {
            if (obj[key] === '') obj[key] = null
        })
    }
    const handleSubmit = (investor: InvestorForm & InvestorFormWithId) => {
        nullEmptyStrings(investor)
        investor.name = cleanValue(investor.name)
        if (investor.tempId) {
            // update
            const newInvestors = investors.map((p) => (p.tempId === investor.tempId ? investor : p))
            setFormInvestors(newInvestors)
        } else {
            // create
            investor.tempId = uuidv4()
            const newInvestors = investors.concat(investor)
            setFormInvestors(newInvestors)
        }
    }

    const handleDelete = (investor: InvestorFormWithId) => {
        const newInvestors = investors.filter((p) => p.tempId !== investor.tempId)
        setFormInvestors(newInvestors)
    }
    return (
        <Stack>
            <Stack direction='row' spacing={2}>
                {!noInvestorsNamedChecked && (
                    <Button variant='outlined' onClick={() => setShowInputArea(!showInputArea)}>
                        {showInputArea ? 'Dölj' : 'Visa'} massinput-fält
                    </Button>
                )}
                {(!investors || investors.length === 0) && (
                    <RegisteredCheckBox
                        name='noInvestorsNamed'
                        label='Inga kända investerare'
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setNoInvestorsNamedChecked && setNoInvestorsNamedChecked(event.target.checked)
                        }
                    />
                )}
            </Stack>
            <Grid container spacing={1}>
                {showInputArea && (
                    <>
                        <Grid size={3}>
                            <Stack spacing={2} sx={{ width: '100%', mt: 4 }}>
                                <TextField
                                    sx={{ width: '100%' }}
                                    label='Investare, input'
                                    multiline
                                    maxRows={3}
                                    value={inputInvestors}
                                    onChange={(event) => {
                                        setMassInputError(null)
                                        setInputInvestors(event.target.value)
                                    }}
                                />
                                <Typography variant='body2'>
                                    Kopiera rubrikerna ovan till ett kalkylblad för rätt struktur, fyll i och kopiera in
                                    här.
                                </Typography>
                                {allowCommaSeparatedNames ? (
                                    <Typography variant='body2'>
                                        Alternativt kopiera in en lista av kommaseparerade namn (RE/ABB/AE).
                                    </Typography>
                                ) : null}
                                {massInputError ? (
                                    <Typography variant='body2' color='error'>
                                        {massInputError}
                                    </Typography>
                                ) : null}
                            </Stack>
                        </Grid>
                        <Grid size={1}>
                            <Button
                                variant='contained'
                                color='primary'
                                fullWidth
                                onClick={handleTransfer}
                                sx={{ mt: 4 }}
                            >
                                &gt;&gt;
                            </Button>
                        </Grid>
                    </>
                )}
                <Grid size={showInputArea ? 8 : 12}>
                    {!noInvestorsNamedChecked && (
                        <InvestorsTable
                            issue={issue}
                            currency={currency}
                            investors={investors}
                            isFeeLevelEnabled={isFeeLevelEnabled}
                            isLeoEnabled={isLeoEnabled}
                            isWithoutRightsEnabled={isWithoutRightsEnabled}
                            isPercentageEnabled={isPercentageEnabled}
                            isSellerEnabled={isSellerEnabled}
                            isPutOptionEnabled={isPutOptionEnabled}
                            sanityChecker={sanityChecker}
                            sx={{ width: '100%' }}
                            handleSubmit={handleSubmit}
                            handleDelete={handleDelete}
                            validateName={validateName}
                            validateAmount={validateAmount}
                            validateFeeLevel={validateFeeLevel}
                            validateLeo={validateLeo}
                        />
                    )}
                </Grid>
                {entityType === 'issue' && issue ? (
                    <>
                        <ShouldShow issueType={issue.type} issueField='investorsAfterTerms'>
                            <Grid size={12}>
                                <RegisteredCheckBox
                                    name='investorsAfterTerms'
                                    label='Uppdaterat i samband med villkor'
                                    onChange={(event) => {
                                        if (!event.target.checked) {
                                            formMethods.setValue('investorsAfterProspectus', false)
                                        }
                                    }}
                                />
                            </Grid>
                        </ShouldShow>
                        <ShouldShow issueType={issue.type} issueField='investorsAfterProspectus'>
                            <Grid size={12}>
                                <RegisteredCheckBox
                                    name='investorsAfterProspectus'
                                    label='Uppdaterat i samband med prospekt'
                                    onChange={(event) => {
                                        if (event.target.checked) {
                                            formMethods.setValue('investorsAfterTerms', true)
                                        }
                                    }}
                                />
                            </Grid>
                        </ShouldShow>
                    </>
                ) : null}
            </Grid>
        </Stack>
    )
}

export default InvestorsFields
